import React, { useCallback, useEffect, useRef } from "react";
import "./ColorPicker.css";

function hsvToHex(h, s, v) {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let [r, g, b] = [0, 0, 0];
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const toHex = (n) => Math.round((n + m) * 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function hexToHsv(hex) {
  const n = parseInt(hex.replace("#", ""), 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const v = max;
  const s = max === 0 ? 0 : d / max;
  return { h, s, v };
}

// A drag-anywhere-on-document handler: fires onMove with the pointer's
// fractional position (0..1, clamped) inside the target element's box.
const useDragBox = (ref, onMove) => {
  const dragging = useRef(false);

  const compute = useCallback((e) => {
    const rect = ref.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
    onMove(x, y);
  }, [ref, onMove]);

  useEffect(() => {
    const move = (e) => { if (dragging.current) compute(e); };
    const up = () => { dragging.current = false; };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    document.addEventListener("touchmove", move);
    document.addEventListener("touchend", up);
    return () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", up);
    };
  }, [compute]);

  const start = (e) => { dragging.current = true; compute(e); };
  return start;
};

// A real HSV color picker: a saturation/value square (for the currently
// selected hue) plus a hue strip, rather than the browser's flat native
// <input type="color"> swatch. Calls onChange(hex) as the admin drags.
const ColorPicker = ({ hex, onChange }) => {
  const { h, s, v } = hexToHsv(hex || "#7c3aed");
  const svRef = useRef(null);
  const hueRef = useRef(null);

  const onSvMove = useCallback((x, y) => {
    onChange(hsvToHex(h, x, 1 - y));
  }, [h, onChange]);

  const onHueMove = useCallback((x) => {
    onChange(hsvToHex(x * 360, s, v));
  }, [s, v, onChange]);

  const startSv = useDragBox(svRef, onSvMove);
  const startHue = useDragBox(hueRef, onHueMove);

  return (
    <div className="cp-wrap">
      <div
        ref={svRef}
        className="cp-sv"
        style={{ background: `hsl(${h}, 100%, 50%)` }}
        onMouseDown={startSv}
        onTouchStart={startSv}
      >
        <div className="cp-sv-white" />
        <div className="cp-sv-black" />
        <div
          className="cp-sv-thumb"
          style={{ left: `${s * 100}%`, top: `${(1 - v) * 100}%`, background: hex }}
        />
      </div>
      <div
        ref={hueRef}
        className="cp-hue"
        onMouseDown={startHue}
        onTouchStart={startHue}
      >
        <div className="cp-hue-thumb" style={{ left: `${(h / 360) * 100}%`, background: `hsl(${h},100%,50%)` }} />
      </div>
      <div className="cp-value-row">
        <span className="cp-swatch" style={{ background: hex }} />
        <input
          className="cp-hex-input"
          type="text"
          value={hex}
          maxLength={7}
          onChange={(e) => {
            const v = e.target.value;
            if (/^#[0-9a-fA-F]{0,6}$/.test(v)) onChange(v);
          }}
        />
      </div>
    </div>
  );
};

export default ColorPicker;
